import './signUpReturningUserPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { PhoneNumberDisabledInput } from '../../components/PhoneNumberDisabledInput/PhoneNumberDisabledInput';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignUpStore from '../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { UserExistsExceptionCode } from '../../store/userStore/enums/UserExistsExceptionCode';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { userExistsException } = useUserStore();

  const { primaryPhoneNumber, primaryEmailAddress } = useSignUpStore();

  const existingPhoneNumber = useMemo((): boolean => {
    return (
      userExistsException === UserExistsExceptionCode.PHONE_NUMBER_REGISTERED ||
      userExistsException === UserExistsExceptionCode.BOTH_REGISTERED
    );
  }, [userExistsException]);

  const existingEmailAddress = useMemo((): boolean => {
    return (
      userExistsException ===
        UserExistsExceptionCode.EMAIL_ADDRESS_REGISTERED ||
      userExistsException === UserExistsExceptionCode.BOTH_REGISTERED
    );
  }, [userExistsException]);

  const back = (): void => {
    navigate('/sign-up');
  };

  const signIn = (): void => {
    usePinStore.setState({
      primaryPhoneNumber: existingPhoneNumber ? primaryPhoneNumber : null,
    });

    navigate('/sign-in');
  };

  return (
    <div className="sign-up-returning-user-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            trailing={
              <Link
                data-testid={
                  locator.pages.signUpReturningUser.header.signInLink
                }
                to="/sign-in"
              >
                {t('SignUpReturningUser.signin')}
              </Link>
            }
            showLogo
          />

          <main>
            <figure>
              <img
                alt={t('imageAlts.heroReference')}
                src={heroReferenceMessage}
              />
            </figure>
            <form
              data-testid={locator.pages.signUpReturningUser.form.root}
              noValidate
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.signUpReturningUser.form.h1}>
                  {t('SignUpReturningUser.title')}
                </h1>
                <h2 data-testid={locator.pages.signUpReturningUser.form.h2}>
                  {t('SignUpReturningUser.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  {existingPhoneNumber && (
                    <div style={{ marginBottom: '2rem' }}>
                      <PhoneNumberDisabledInput
                        inputLabelKey="SignUpReturningUser.phone"
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.signUpReturningUser.form.phoneInput,
                          },
                        }}
                        value={primaryPhoneNumber}
                      />
                    </div>
                  )}

                  {existingEmailAddress && (
                    <TextField
                      color="primary"
                      label={t('SignUpReturningUser.email')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.signUpReturningUser.form.phoneInput,
                        },
                      }}
                      style={{ pointerEvents: 'none' }}
                      value={primaryEmailAddress}
                      variant="outlined"
                      disabled
                      fullWidth
                    />
                  )}
                </div>

                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.signUpReturningUser.form.backButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={back}
                  >
                    {t('SignUpReturningUser.back')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.signUpReturningUser.form.signInButton
                    }
                    fullWidth
                    onClick={signIn}
                  >
                    {t('SignUpReturningUser.signin')}
                  </Button>
                </div>
              </div>
              <footer
                data-testid={locator.pages.signUpReturningUser.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignUpReturningUserPage';
